import { useRef } from "react";
import { useSnapshot } from "valtio";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import {
  X,
  Sun,
  Moon,
  Download,
  Upload,
  RotateCcw,
  Palette,
} from "lucide-react";
import {
  themeEditorStore,
  closeThemeEditor,
  setEditingMode,
  updateColor,
  updateRadius,
  exportCurrentTheme,
  importTheme,
  resetToDefault,
} from "@/lib/stores/theme-editor";
import { COLOR_GROUPS, type ThemeColors } from "@/lib/theme/types";
import { ColorSection } from "./ColorSection";
import { ThemePresets } from "./ThemePresets";

/**
 * Radius options in rem
 */
const RADIUS_OPTIONS = [
  { value: "0", label: "0" },
  { value: "0.3rem", label: "0.3" },
  { value: "0.5rem", label: "0.5" },
  { value: "0.75rem", label: "0.75" },
  { value: "1rem", label: "1.0" },
];

/**
 * Main theme editor panel component
 */
export function ThemeEditorPanel() {
  const { currentTheme, editingMode, hasChanges } =
    useSnapshot(themeEditorStore);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentColors =
    editingMode === "dark" ? currentTheme.dark : currentTheme.light;

  const handleColorChange = (key: keyof ThemeColors, value: string) => {
    updateColor(key, value);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await importTheme(file);
      } catch (error) {
        console.error("Failed to import theme:", error);
      }
    }
    // Reset input so the same file can be selected again
    e.target.value = "";
  };

  return (
    <form
      className="flex h-full flex-col bg-background"
      data-1p-ignore
      data-lpignore="true"
      onSubmit={(e) => e.preventDefault()}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-primary" />
          <h2 className="font-semibold">Theme Editor</h2>
          {hasChanges && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
              Modified
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={closeThemeEditor}
          className="h-8 w-8"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Light/Dark Mode Toggle */}
      <div className="flex items-center justify-center gap-2 border-b px-4 py-2">
        <Button
          variant={editingMode === "light" ? "default" : "ghost"}
          size="sm"
          onClick={() => setEditingMode("light")}
          className="gap-1"
        >
          <Sun className="h-4 w-4" />
          Light
        </Button>
        <Button
          variant={editingMode === "dark" ? "default" : "ghost"}
          size="sm"
          onClick={() => setEditingMode("dark")}
          className="gap-1"
        >
          <Moon className="h-4 w-4" />
          Dark
        </Button>
      </div>

      {/* Tabs */}
      <Tabs
        defaultValue="colors"
        className="flex flex-1 flex-col overflow-hidden"
      >
        <TabsList className="mx-4 mt-3 grid w-auto grid-cols-3">
          <TabsTrigger value="colors">Colors</TabsTrigger>
          <TabsTrigger value="radius">Radius</TabsTrigger>
          <TabsTrigger value="presets">Presets</TabsTrigger>
        </TabsList>

        <ScrollArea className="flex-1">
          {/* Colors Tab */}
          <TabsContent value="colors" className="m-0 p-4">
            <div className="space-y-6">
              {COLOR_GROUPS.map((group) => (
                <ColorSection
                  key={group.title}
                  title={group.title}
                  colors={group.colors}
                  values={currentColors as ThemeColors}
                  onChange={handleColorChange}
                />
              ))}
            </div>
          </TabsContent>

          {/* Radius Tab */}
          <TabsContent value="radius" className="m-0 p-4">
            <div className="space-y-6">
              <div className="space-y-4">
                <Label className="text-sm font-medium">Border Radius</Label>
                <p className="text-sm text-muted-foreground">
                  Controls the roundness of buttons, cards, inputs, and other
                  components.
                </p>

                <div className="flex flex-wrap gap-2">
                  {RADIUS_OPTIONS.map((option) => (
                    <Button
                      key={option.value}
                      variant={
                        currentTheme.radius === option.value
                          ? "default"
                          : "outline"
                      }
                      size="sm"
                      onClick={() => updateRadius(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>

                <div className="text-xs text-muted-foreground">
                  Current: {currentTheme.radius}
                </div>
              </div>

              {/* Preview shapes */}
              <Separator />
              <div className="space-y-3">
                <Label className="text-sm font-medium">Preview</Label>
                <div className="flex flex-wrap gap-4">
                  <div
                    className="h-16 w-16 bg-primary"
                    style={{ borderRadius: `var(--radius)` }}
                  />
                  <div
                    className="h-16 w-24 border-2 border-border bg-card"
                    style={{ borderRadius: `var(--radius)` }}
                  />
                  <Button size="sm">Button</Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Presets Tab */}
          <TabsContent value="presets" className="m-0 p-4">
            <ThemePresets />
          </TabsContent>
        </ScrollArea>
      </Tabs>

      {/* Footer Actions */}
      <div className="flex flex-wrap gap-2 border-t p-3">
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileChange}
          className="hidden"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          className="gap-1"
        >
          <Upload className="h-3.5 w-3.5" />
          Import
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={exportCurrentTheme}
          className="gap-1"
        >
          <Download className="h-3.5 w-3.5" />
          Export
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={resetToDefault}
          className="gap-1"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset
        </Button>
      </div>
    </form>
  );
}
